import PropTypes from "prop-types";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import { Chip, Grid, Icon, MenuItem, OutlinedInput, Select, Stack } from "@mui/material";
import { useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import StyledTextarea from "components/StyledTextarea";
import dayjs from 'dayjs';
import { DatePicker } from "@mui/x-date-pickers";
import Dropzone from "react-dropzone";
import StyledImagePreview from "components/StyledImagePreview";
import Loader from "components/loader";
import SoftButton from "components/SoftButton";

function BlogAddEdit({ currentBlogData, allBlogCategories, noBlogImage, hasValidationError, validationErrors }) {
    const editorRef = useRef(null);
    const [defaultCategoryId, setDefaultCategoryId] = useState(currentBlogData.blog_category_id ?? 0);
    const [allKeywords, setAllKeywords] = useState(currentBlogData.seo_keywords ?? []);
    const [defaultSlug, setDefaultSlug] = useState(currentBlogData.slug ?? '');
    const defaultImagePreview = noBlogImage;
    const defaultImageName = 'No Image Available';
    const [imagePreview, setImagePreview] = useState(
        currentBlogData.image !== '' && currentBlogData.image !== null
            ? currentBlogData.image
            : defaultImagePreview
    );
    const [imageName, setImageName] = useState(
        currentBlogData.image_name !== '' && currentBlogData.image_name !== null
            ? currentBlogData.image_name
            : defaultImageName
    );
    const [isDropOverImage, setIsDropOverImage] = useState(false);
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };
    const [imageLoader, setImageLoader] = useState(false);
    const handleOnDrag = (acceptedFiles) => {
        setImageLoader(true);
        const reader = new FileReader();
        reader.onload = async function () {
            const blogBlob = new Blob([reader.result]);
            setImagePreview(URL.createObjectURL(blogBlob));
            setImageLoader(false);
            setIsDropOverImage(false);
        };
        reader.readAsArrayBuffer(acceptedFiles[0]);
        setImageName(acceptedFiles[0].name);
        currentBlogData['image'] = acceptedFiles[0];
        currentBlogData['image_name'] = acceptedFiles[0].name;
        currentBlogData['isNewImage'] = true;
    };
    const handleBlogImageRemove = (event) => {
        setImageLoader(true);
        setImagePreview(defaultImagePreview);
        setImageName(defaultImageName);
        setImageLoader(false);
    }

    return <>
        <SoftBox component="form" role="form" style={{
            "margin": "0 16px"
        }}>
            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="stretch"
                spacing={1}>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Blog Title
                            </SoftTypography>
                        </SoftBox>
                        <SoftInput
                            type="text"
                            placeholder="Blog Title"
                            defaultValue={currentBlogData.title}
                            onChange={
                                (event) => {
                                    currentBlogData.title = event.target.value
                                    currentBlogData.slug = event.target.value.replace(/\s+/g, '-').toLowerCase()
                                    setDefaultSlug(currentBlogData.slug);
                                }
                            } />
                        {
                            hasValidationError && Object.keys(validationErrors).includes('title') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Blog Title is required and It should be valid title
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Blog Slug
                            </SoftTypography>
                        </SoftBox>
                        <SoftInput
                            type="text"
                            placeholder="Blog Slug"
                            defaultValue={defaultSlug}
                            value={defaultSlug}
                            onChange={
                                (event) => {
                                    currentBlogData.slug = event.target.value.replace(/\s+/g, '-').toLowerCase()
                                    setDefaultSlug(currentBlogData.slug);
                                }
                            } />
                        {
                            hasValidationError && Object.keys(validationErrors).includes('slug') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Blog Slug is required and It should be valid Slug
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Blog Category
                            </SoftTypography>
                        </SoftBox>
                        <Select
                            displayEmpty
                            value={defaultCategoryId}
                            onChange={(event) => {
                                currentBlogData.blog_category_id = event.target.value;
                                setDefaultCategoryId(currentBlogData.blog_category_id);
                            }}
                            input={<OutlinedInput />}
                            MenuProps={MenuProps}
                        >
                            <MenuItem value="0">
                                Select Blog Category
                            </MenuItem>
                            {allBlogCategories.map((singleBlog) => (
                                <MenuItem
                                    key={singleBlog.id}
                                    value={singleBlog.id}
                                >
                                    {singleBlog.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {
                            hasValidationError && Object.keys(validationErrors).includes('blog_category_id') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Select at least one Blog Category
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Publish Date
                            </SoftTypography>
                        </SoftBox>
                        <DatePicker
                            defaultValue={dayjs(currentBlogData.publish_date)}
                            onChange={(newValue) => {
                                currentBlogData.publish_date = newValue.format('YYYY-MM-DD');
                                currentBlogData.isPublishDateChange = true;
                            }}
                        />
                        {
                            hasValidationError && Object.keys(validationErrors).includes('publish_date') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Please select valid Blog Publish Date
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Blog Image
                            </SoftTypography>
                        </SoftBox>
                        <SoftBox mb={1} style={{ 'padding': '15px 15px 15px 0' }}>
                            {
                                imageName === 'No Image Available' ?
                                    <Dropzone
                                        onDragEnter={() => {
                                            setIsDropOverImage(true);
                                        }}
                                        onDragLeave={() => {
                                            setIsDropOverImage(false);
                                        }}
                                        onDrop={handleOnDrag}>
                                        {({ getRootProps, getInputProps }) => (
                                            <section style={{
                                                'width': '100%',
                                                'height': '160px',
                                                'display': 'flex',
                                                'alignItems': 'center',
                                                'border': '2px dashed #344767',
                                                'borderRadius': '16px'
                                            }}>
                                                <div {...getRootProps()} style={{
                                                    'width': 'inherit',
                                                    'height': 'inherit',
                                                    'display': 'flex',
                                                    'alignItems': 'center',
                                                    'textAlign': 'center',
                                                }}>
                                                    <input {...getInputProps()} />
                                                    <p style={{
                                                        'margin': '32px',
                                                        'width': 'inherit'
                                                    }}>
                                                        {
                                                            isDropOverImage ?
                                                                'Drop Here' :
                                                                'Drag & drop some files here, or click to select files'
                                                        }
                                                    </p>
                                                </div>
                                            </section>
                                        )}
                                    </Dropzone> :
                                    !imageLoader && <SoftBox mb={2} style={{
                                        'border': '2px dashed #344767',
                                        'borderRadius': '16px',
                                        'height': '64px',
                                        'padding': '0 16px',
                                        'display': 'flex',
                                        'alignItems': 'center',
                                    }}>
                                        <SoftBox mb={1} style={{ 'flex': 2 }}>
                                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                {imageName}
                                            </SoftTypography>
                                        </SoftBox>
                                        <SoftButton variant="gradient" color="error" iconOnly={true} onClick={handleBlogImageRemove}>
                                            <Icon sx={{ cursor: "pointer", fontWeight: "bold" }} fontSize="small" onClick={handleBlogImageRemove}>
                                                delete
                                            </Icon>
                                        </SoftButton>
                                    </SoftBox>
                            }
                            {
                                imageLoader && <Loader classes='black' />
                            }
                            {
                                hasValidationError && Object.keys(validationErrors).includes('image') && <SoftBox>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                        Please valid image which should be in png, jpg, or jpeg
                                    </SoftTypography>
                                </SoftBox>
                            }
                        </SoftBox>
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Preview Image
                            </SoftTypography>
                        </SoftBox>
                        <SoftBox mb={1} style={{ 'padding': '15px 15px 15px 0' }}>
                            <StyledImagePreview
                                imagePreview={imagePreview}
                                imageName={imageName} />
                        </SoftBox>
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Description
                            </SoftTypography>
                        </SoftBox>
                        <Editor
                            apiKey={process.env.REACT_APP_TINY_MCS}
                            onInit={(evt, editor) => editorRef.current = editor}
                            initialValue={currentBlogData.description}
                            init={{
                                height: 300,
                                menubar: true,
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                                ],
                                toolbar: 'undo redo | blocks | ' +
                                    'bold italic forecolor | alignleft aligncenter ' +
                                    'alignright alignjustify | bullist numlist outdent indent | ' +
                                    'removeformat | help',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                            }}
                            onChange={(event) => currentBlogData.description = event.target.getContent()}
                        />
                        {
                            hasValidationError && Object.keys(validationErrors).includes('description') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Blog Description is required and It should be valid Description
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Additional Tags
                            </SoftTypography>
                        </SoftBox>
                        <Editor
                            apiKey={process.env.REACT_APP_TINY_MCS}
                            onInit={(evt, editor) => editorRef.current = editor}
                            initialValue={currentBlogData.additional_tags}
                            init={{
                                height: 300,
                                menubar: true,
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                                ],
                                toolbar: 'undo redo | blocks | ' +
                                    'bold italic forecolor | alignleft aligncenter ' +
                                    'alignright alignjustify | bullist numlist outdent indent | ' +
                                    'removeformat | help',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                            }}
                            onChange={(event) => currentBlogData.additional_tags = event.target.getContent()}
                        />
                        {
                            hasValidationError && Object.keys(validationErrors).includes('additional_tags') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Blog Additional Tags are required and It should be valid Additional Tags
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Custom Script
                            </SoftTypography>
                        </SoftBox>
                        <StyledTextarea
                            aria-label="minimum height"
                            minRows={5}
                            placeholder="Custom Script"
                            defaultValue={currentBlogData.custom_script}
                            onChange={(event) => currentBlogData.custom_script = event.target.value} />
                        {
                            hasValidationError && Object.keys(validationErrors).includes('custom_script') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Blog Custom Script is required and It should be valid Custom Script
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Short Description
                            </SoftTypography>
                        </SoftBox>
                        <StyledTextarea
                            aria-label="minimum height"
                            minRows={5}
                            placeholder="Short Description"
                            defaultValue={currentBlogData.short_description}
                            onChange={(event) => currentBlogData.short_description = event.target.value} />
                        {
                            hasValidationError && Object.keys(validationErrors).includes('short_description') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Blog Short Description is required and It should be valid Short Description
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                SEO Title
                            </SoftTypography>
                        </SoftBox>
                        <SoftInput
                            type="text"
                            placeholder="SEO Title"
                            defaultValue={currentBlogData.seo_title}
                            onChange={(event) => currentBlogData.seo_title = event.target.value} />
                        {
                            hasValidationError && Object.keys(validationErrors).includes('seo_title') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Blog SEO Title is required and It should be valid SEO title
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                SEO Keywords
                            </SoftTypography>
                        </SoftBox>
                        <SoftInput
                            type="text"
                            placeholder="SEO Keywords"
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    currentBlogData.seo_keywords.push(event.target.value);
                                    setAllKeywords(currentBlogData.seo_keywords);
                                    event.target.value = '';
                                }
                            }} />
                        {
                            allKeywords.length > 0 && <SoftBox mt={1}>
                                <Stack direction="row" spacing={1} style={{ 'flexWrap': 'wrap' }}>
                                    {
                                        allKeywords.map((singleSeoKeyword, index) => (
                                            <Chip
                                                label={singleSeoKeyword}
                                                onDelete={() => {
                                                    currentBlogData.seo_keywords = currentBlogData.seo_keywords.filter((seoKeyword) => seoKeyword !== singleSeoKeyword);
                                                    setAllKeywords(currentBlogData.seo_keywords);
                                                }}
                                                key={singleSeoKeyword + '_' + index} />
                                        ))
                                    }
                                </Stack>
                            </SoftBox>
                        }
                        {
                            hasValidationError && Object.keys(validationErrors).includes('seo_keywords') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Blog SEO Keywords are required and It should be valid SEO Keywords
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <SoftBox mb={2}>
                        <SoftBox mb={1}>
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                SEO Description
                            </SoftTypography>
                        </SoftBox>
                        <StyledTextarea
                            aria-label="minimum height"
                            minRows={5}
                            placeholder="SEO Description"
                            defaultValue={currentBlogData.seo_description}
                            onChange={(event) => currentBlogData.seo_description = event.target.value} />
                        {
                            hasValidationError && Object.keys(validationErrors).includes('seo_description') && <SoftBox>
                                <SoftTypography component="label" variant="caption" fontWeight="bold" color="error">
                                    Blog SEO Description is required and It should be valid SEO Description
                                </SoftTypography>
                            </SoftBox>
                        }
                    </SoftBox>
                </Grid>
            </Grid>
        </SoftBox >
    </>;
}
BlogAddEdit.defaultProps = {
    currentBlogData: {},
    allBlogCategories: [],
    noBlogImage: '',
    hasValidationError: false,
    validationErrors: []
};
BlogAddEdit.propTypes = {
    currentBlogData: PropTypes.any.isRequired,
    allBlogCategories: PropTypes.array.isRequired,
    noBlogImage: PropTypes.string.isRequired,
    hasValidationError: PropTypes.bool.isRequired,
    validationErrors: PropTypes.array.isRequired
};

export default BlogAddEdit;